import { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'motion/react';
import { C } from '../../constants/colors.js';
import { F, N } from '../../constants/fonts.js';
import { Btn } from '../ui/Btn.jsx';
import { FinleyInsightBanner } from '../ui/FinleyInsightBanner.jsx';
import { PB } from '../ui/ProgressBar.jsx';
import { getDueWords } from '../../lib/leitner.js';
import { getInsightMessage } from '../../config/insightMessages.js';
import { getSpellingWords } from '../../lib/spelling.js';
import { useTTS } from '../../hooks/useTTS.js';
import { rqConfettiBurst, rqSpawnParticles } from '../../lib/animations.js';
import { shuffle, rb } from '../../lib/utils.js';
import { popIn, tapBounce } from '../../lib/motionPresets.js';

function spawnBubbleParticles(container, cx, cy) {
  const colors = ['#FF6B35','#7B2FBE','#00D4AA','#FFD166','#4ADE80','#60A5FA'];
  for (let i = 0; i < 12; i++) {
    const p = document.createElement('div');
    const size = 5 + Math.random() * 7;
    const angle = (Math.PI*2*i)/12 + (Math.random()-0.5)*0.4;
    const dist = 35 + Math.random() * 45;
    p.style.cssText = `position:absolute;width:${size}px;height:${size}px;border-radius:50%;background:${colors[i%colors.length]};left:${cx}px;top:${cy}px;pointer-events:none;z-index:50;`;
    container.appendChild(p);
    p.animate([{transform:'translate(-50%,-50%) scale(1)',opacity:1},{transform:`translate(${Math.cos(angle)*dist-size/2}px,${Math.sin(angle)*dist-size/2}px) scale(0)`,opacity:0}],{duration:500+Math.random()*200,easing:'cubic-bezier(0,0.5,0.5,1)',fill:'forwards'});
    setTimeout(() => p.remove(), 800);
  }
}

const BUBBLE_SIZE = 100;
const MIN_BUBBLE_SPACING = BUBBLE_SIZE + 15;
const SAFE_TOP = 0.45;
const SAFE_BOTTOM = 0.1;

function getBubbleFontSize(word) {
  const length = (word || '').length;
  if (length <= 3) return '1.375rem';
  if (length <= 5) return '1.125rem';
  if (length <= 7) return '1rem';
  if (length <= 9) return '0.8125rem';
  if (length <= 11) return '0.6875rem';
  return '0.625rem';
}

function createBubblePositions(count, width, height) {
  const halfX = BUBBLE_SIZE / 2 / width;
  const halfY = BUBBLE_SIZE / 2 / height;
  const minX = halfX;
  const maxX = 1 - halfX;
  const minY = Math.max(SAFE_TOP, halfY);
  const maxY = Math.max(minY, 1 - SAFE_BOTTOM - halfY);
  const positions = [];
  for (let index = 0; index < count; index++) {
    let candidate = null;
    let bestCandidate = null;
    let bestDistance = -1;
    for (let attempt = 0; attempt < 20; attempt++) {
      const next = {
        x: Math.min(maxX, Math.max(minX, rb(minX, maxX))),
        y: Math.min(maxY, Math.max(minY, rb(minY, maxY)))
      };
      const nearestDistance = positions.reduce((nearest, existing) => {
        const dx = (existing.x - next.x) * width;
        const dy = (existing.y - next.y) * height;
        const distance = Math.hypot(dx, dy);
        return Math.min(nearest, distance);
      }, Number.POSITIVE_INFINITY);
      if (nearestDistance >= MIN_BUBBLE_SPACING) {
        candidate = next;
        break;
      }
      if (nearestDistance > bestDistance) {
        bestCandidate = next;
        bestDistance = nearestDistance;
      }
    }
    positions.push(candidate || bestCandidate || {
      x: Math.min(maxX, Math.max(minX, 0.2 + index * 0.18)),
      y: Math.min(maxY, Math.max(minY, index % 2 === 0 ? minY + 0.08 : maxY - 0.08))
    });
  }
  return positions;
}

export function BubblePop({theme, tracker, onComplete, onRecord, profile, sessionActivityIndex = 0, isPlaylistSession = false}) {
  const ref = useRef(null);
  const [sz, setSz] = useState({w:380,h:450});
  const [bubs, setBubs] = useState([]);
  const [target, setTarget] = useState("");
  const [score, setScore] = useState(0);
  const [round, setRound] = useState(0);
  const [done, setDone] = useState(false);
  const [insightMessage, setInsightMessage] = useState('');
  const [insightVisible, setInsightVisible] = useState(false);
  const bRef = useRef([]);
  const TOT = tracker.chillMode ? 4 : 8;
  const rStart = useRef(Date.now());
  const age = profile?.age || 7;
  const pid = profile?.id || "default";
  const tutKey = 'rq:bubblePop-tutorial-seen:' + pid;
  const [showTutorial, setShowTutorial] = useState(() => { try { return !localStorage.getItem(tutKey); } catch { return false; } });
  const [currentIsDue, setCurrentIsDue] = useState(false);
  const { speak, stop } = useTTS();

  useEffect(() => {
    if (!showTutorial) return;
    const t = setTimeout(() => { setShowTutorial(false); try { localStorage.setItem(tutKey, '1'); } catch {} }, 3000);
    return () => clearTimeout(t);
  }, [showTutorial, tutKey]);

  const dismissTutorial = () => { setShowTutorial(false); try { localStorage.setItem(tutKey, '1'); } catch {} };

  const lv = tracker.currentLevel;
  const BASEWORDS = lv <= 3 ? ["the","and","is","it","in","to","my","we","go","cat","dog","big","run","hot","was","you","are","not","can"]
    : lv <= 6 ? ["jump","swim","nest","star","wave","fast","black","crisp","shout","think","brave","storm","flame","creek","cloud","frost"]
    : lv <= 9 ? ["sunset","rabbit","garden","adventure","champion","discover","important","enormous","suddenly","celebrate","examine","treasure","knowledge","brilliant","telescope"]
    : ["revolutionary","philanthropist","unprecedented","contemporary","hypothetical","metamorphosis","extraordinary","philosophical","sophisticated","fundamental","catastrophe","magnificent","determination","consequences","architecture"];

  const bColors = [theme.color, C.primary, C.secondary, C.success, C.purple];

  useEffect(() => {
    const el = ref.current; if (!el) return;
    const ro = new ResizeObserver(() => setSz({w:el.clientWidth,h:el.clientHeight}));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const gen = useCallback(() => {
    const dueWords = getDueWords(tracker.wordMastery, age, 10).filter(w => w && w.length >= 2 && w.length <= 14 && !w.includes(' '));
    const weak = (tracker.weakWords || []).filter(w => w && w.length >= 2 && w.length <= 14 && !w.includes(' '));
    const leitnerPool = dueWords.length > 0 ? dueWords : weak.length > 0 ? weak : [];
    const pool = leitnerPool.length >= 4 ? leitnerPool : [...new Set([...leitnerPool, ...BASEWORDS])];
    const spWords = getSpellingWords(profile?.id).map(w => w.word).filter(w => w && w.length >= 2 && w.length <= 14 && !w.includes(' '));
    const enhancedPool = spWords.length > 0 ? [...spWords, ...spWords, ...pool] : pool;
    const t = shuffle(enhancedPool)[0];
    setCurrentIsDue(dueWords.includes(t));
    const d = shuffle(enhancedPool.filter(w => w !== t)).slice(0, 3);
    const ws = shuffle([t, ...d]);
    const cw = ref.current?.clientWidth || sz.w || 380;
    const ch = ref.current?.clientHeight || sz.h || 450;
    const positions = createBubblePositions(ws.length, cw, ch);
    const nb = ws.map((word, i) => ({
      id: `${Date.now()}-${i}`, word, isT: word === t,
      x: positions[i].x, y: positions[i].y, vx: rb(-.12,.12), vy: rb(-.1,.1),
      sz: BUBBLE_SIZE, color: bColors[i % bColors.length],
      st: "f", ph: Math.random() * Math.PI * 2, wb: 0, wt: 0
    }));
    setTarget(t); setBubs(nb); bRef.current = nb; rStart.current = Date.now();
  }, [tracker.wordMastery, tracker.weakWords, age, lv, theme.color, profile?.id, sz.w, sz.h]);

  useEffect(() => { gen(); }, []);

  useEffect(() => {
    if (!target) return;
    const triggers = [];
    if (currentIsDue) triggers.push('leitner_review');
    if (round > 0 && round % 3 === 0) triggers.push('periodic');
    const msg = getInsightMessage({
      activityKey: `bubbles:${sessionActivityIndex}:${round}:${target}`,
      sessionStart: isPlaylistSession && sessionActivityIndex === 0 && round === 0,
      triggers
    });
    if (msg) { setInsightMessage(msg); setInsightVisible(true); }
  }, [target, round, currentIsDue, sessionActivityIndex, isPlaylistSession]);

  useEffect(() => {
    if (!target) return;
    speak(target, { rate: 0.95, pitch: 1.0 });
  }, [target, speak]);

  useEffect(() => () => stop(), [stop]);

  useEffect(() => {
    let last = performance.now(); let raf;
    const tick = (now) => {
      const dt = Math.min((now - last) / 1000, .05); last = now;
      bRef.current = bRef.current.map(ob => {
        const b = {...ob};
        if (b.st !== "f") {
          if (b.st === "w") { b.wt -= dt; b.wb = Math.sin(b.wt*30)*6*Math.max(0,b.wt/.3); if (b.wt <= 0) { b.st = "f"; b.wb = 0; } }
          return b;
        }
        let nx = b.x + b.vx * dt, ny = b.y + b.vy * dt;
        b.ph += dt * .6; ny += Math.sin(b.ph) * .0015;
        const cw = ref.current?.clientWidth || 380; const ch = ref.current?.clientHeight || 450;
        const mx = b.sz/2/cw, my = b.sz/2/ch;
        if (nx < mx) { nx = mx; b.vx = Math.abs(b.vx) * .8; }
        if (nx > 1-mx) { nx = 1-mx; b.vx = -Math.abs(b.vx) * .8; }
        if (ny < Math.max(SAFE_TOP, my)) { ny = Math.max(SAFE_TOP, my); b.vy = Math.abs(b.vy) * .8; }
        if (ny > 1-SAFE_BOTTOM-my) { ny = 1-SAFE_BOTTOM-my; b.vy = -Math.abs(b.vy) * .8; }
        return {...b, x:nx, y:ny};
      });
      setBubs([...bRef.current]); raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const pop = useCallback((id) => {
    const b = bRef.current.find(x => x.id === id);
    if (!b || b.st !== "f") return;
    const ms = Date.now() - rStart.current;
    if (b.isT) {
      b.st = "popping"; setBubs([...bRef.current]);
      const el = ref.current;
      setTimeout(() => {
        if (el) {
          const bEl = el.querySelector(`[data-bid="${id}"]`);
          if (bEl) { const r = bEl.getBoundingClientRect(); const cr = el.getBoundingClientRect(); spawnBubbleParticles(el, r.left-cr.left+r.width/2, r.top-cr.top+r.height/2); rqSpawnParticles(bEl, 12, theme.color); }
        }
        b.st = "p"; setBubs([...bRef.current]); rqConfettiBurst({count:8,spread:30});
      }, 120);
      setScore(s => s + 10); onRecord("bubbles", b.word, true, ms);
      const nr = round + 1; setRound(nr);
      if (nr >= TOT) setTimeout(() => setDone(true), 600);
      else setTimeout(() => gen(), 700);
    } else {
      b.st = "w"; b.wt = .3; onRecord("bubbles", b.word, false, ms); setBubs([...bRef.current]);
    }
  }, [round, gen, onRecord, theme.color]);

  if (done) return (
    <div style={{textAlign:"center",padding:"36px 24px"}}>
      <div style={{fontSize:64,marginBottom:10,animation:"float 2s ease-in-out infinite"}}>🫧</div>
      <h2 style={{fontFamily:F,fontSize:28,color:C.text}}>Bubbles Done!</h2>
      <p style={{fontFamily:F,fontSize:22,color:C.primary}}>Score: {score}</p>
      <Btn onClick={() => onComplete(Math.floor(score/10))} style={{marginTop:20}}>Keep Going →</Btn>
    </div>
  );

  if (showTutorial) return (
    <motion.div {...popIn} onClick={dismissTutorial} style={{textAlign:"center",padding:"60px 24px",cursor:"pointer"}}>
      <div style={{fontSize:80,marginBottom:16,animation:"float 2s ease-in-out infinite"}}>🐬</div>
      <div style={{background:"#fff",borderRadius:20,padding:"20px 24px",maxWidth:320,margin:"0 auto",border:`3px solid ${C.accent2}44`,boxShadow:"0 8px 24px rgba(0,0,0,0.1)"}}>
        <p style={{fontFamily:F,fontSize:20,color:C.text,margin:"0 0 8px"}}>Help me find my word!</p>
        <p style={{fontFamily:N,fontSize:15,color:C.textMid,margin:0}}>Tap the bubble with the right word!</p>
      </div>
      <p style={{fontFamily:N,fontSize:12,color:C.textLight,marginTop:20}}>Tap anywhere to start</p>
    </motion.div>
  );

  return (
    <div ref={ref} style={{position:"relative",height:"100%",minHeight:420,overflow:"hidden",background:`linear-gradient(180deg,${theme.colorLight},#fff)`}}>
      <FinleyInsightBanner message={insightMessage} visible={insightVisible} />
      <div style={{position:"absolute",top:0,left:0,right:0,zIndex:20,padding:"12px 16px",display:"flex",justifyContent:"space-between",alignItems:'flex-start'}}>
        <div style={{display:'flex',flexDirection:'column',gap:6}}><div style={{fontFamily:N,fontSize:11,color:C.textLight}}>SCORE</div><div style={{fontFamily:F,fontSize:24,color:C.primary,lineHeight:1}}>{score}</div></div>
        <div style={{textAlign:"right"}}><div style={{fontFamily:N,fontSize:11,color:C.textLight}}>ROUND</div><div style={{fontFamily:F,fontSize:20,color:C.textMid}}>{round+1}/{TOT}</div></div>
      </div>
      <div style={{position:"absolute",top:68,left:16,right:16,zIndex:20}}><PB current={round} total={TOT} color={theme.color}/></div>
      <div style={{position:"absolute",top:104,left:"50%",transform:"translateX(-50%)",zIndex:20,background:"#fff",padding:"12px 28px",borderRadius:20,border:`3px solid ${theme.color}`,boxShadow:`0 4px 20px ${theme.color}33`,maxWidth:'calc(100% - 32px)'}}>
        <div style={{fontFamily:F,fontSize:16,color:C.textMid,textAlign:"center",marginBottom:2}}>Find:</div>
        <div style={{fontFamily:F,fontSize:34,color:theme.color,textAlign:"center",letterSpacing:1,fontWeight:700,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',maxWidth:'100%'}}>{target}</div>
      </div>
      {bubs.map(b => {
        if (b.st === "p") return null;
        const px = b.x * sz.w, py = b.y * sz.h;
        return (
          <motion.div key={b.id} {...tapBounce} initial={{ opacity: 0, scale: 0.4 }} animate={{ opacity: b.st==="popping" ? 0 : 1, scale: b.st==="popping" ? 0 : 1 }} data-bid={b.id} onClick={() => b.st === "f" && pop(b.id)}
            style={{position:"absolute",left:px-b.sz/2,top:py-b.sz/2,width:b.sz,height:b.sz,borderRadius:"50%",cursor:b.st==="f"?"pointer":"default",transform:b.st==="w"?`scale(.9) rotate(${b.wb}deg)`:b.st==="popping"?"scale(1.3)":"scale(1)",opacity:b.st==="w"?.5:1,transition:b.st==="popping"?"transform 0.12s ease-out":"none",zIndex:5}}>
            <div style={{position:"absolute",inset:0,borderRadius:"50%",background:`radial-gradient(circle at 30% 30%, rgba(255,255,255,0.75) 0%, ${b.color}99 40%, ${b.color}cc 100%)`,border:`3px solid ${b.color}`,boxShadow:`0 4px 16px ${b.color}44, inset 0 -4px 10px rgba(0,0,0,0.1)`,display:"flex",alignItems:"center",justifyContent:"center"}}>
              <div style={{position:"absolute",top:"14%",left:"22%",width:"30%",height:"22%",borderRadius:"50%",background:"rgba(255,255,255,.45)",filter:"blur(3px)"}}/>
              <span className="z-[2] flex max-w-[78%] items-center justify-center text-center font-finley-display font-bold leading-none text-white whitespace-nowrap" style={{fontSize:getBubbleFontSize(b.word),textShadow:"0 2px 4px rgba(0,0,0,.2)",overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',wordBreak:'keep-all',overflowWrap:'normal'}}>{b.word}</span>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}
